import _ from 'lodash';
import React from 'react';
import { ReactComponent as AddIcon } from 'src/assets/icons/addLine.svg';
import { ReactComponent as MenuIcon } from 'src/assets/icons/menuDots.svg';
import { ReactComponent as PencilIcon } from 'src/assets/icons/pencil.svg';
import { Keys, locale } from 'src/i18n';
import keys from 'src/i18n/keys';
import {
  getRevyreModuleLabelByCode,
  getRevyreWorkflowConfig,
} from 'src/models/lmsConfig/queries';
import { ModuleButton } from 'src/models/moduleButtons/types';
import { REVYRE_FORM } from 'src/modules/constants';
import {
  renderDialog,
  setRouteModuleBreadCrumbParams,
} from 'src/workspace/slice';
import { CASE, REVYRE } from './constant';
import {
  shouldShowQuickActions,
  transformQuickAction,
} from './profileCard/queries';

export const getReferredModuleActions = (details, props, dispatch) => {
  const actions: any = [];
  if (!details) return actions;
  if (props.revyre) {
    const { domainType, workflowType } = props;
    const { referredEntityDetails, allowedActions, inputsMap } = details;
    if (domainType === 'workflow') {
      actions.push({
        code: 'createActivity',
        label: locale(Keys.ADD_ACTIVITY),
        buttonProps: { action: 'create' },
        onClick: () => {
          const isCustomRefferedEntity =
            REVYRE.COLLECT_IQ.DETAILS_PAGE_CUSTOM_REFFERED_ENTITIES.includes(
              'activity',
            );
          dispatch(
            renderDialog({
              id: REVYRE_FORM,
              props: {
                scenario: 'activity',
                type: 'detailsCreate',
                domainType: 'activity',
                ...(isCustomRefferedEntity && {
                  buttonMetadata: {
                    parentAssociateWorkflowType: workflowType,
                  },
                }),
              },
            }),
          );
        },
      });
    }

    if (workflowType !== CASE && allowedActions?.length) {
      actions.push({
        code: 'update',
        label: 'Update',
        buttonProps: { action: 'update' },
        icon: <PencilIcon />,
        onClick: () => {
          dispatch(
            renderDialog({
              id: REVYRE_FORM,
              props: {
                type: 'update',
                domainType,
                activityType:
                  domainType === 'activity'
                    ? inputsMap?.activityType
                    : undefined,
              },
            }),
          );
        },
      });
    }

    const showQuickActions = shouldShowQuickActions({
      inputsMap: details?.inputsMap,
      workflowType,
    });

    actions.push(
      ...(referredEntityDetails || []).map((linkedWorkflow) => {
        if (
          REVYRE.COLLECT_IQ.DETAILS_PAGE_CUSTOM_REFFERED_ENTITIES.includes(
            linkedWorkflow.workflowType,
          ) &&
          showQuickActions
        ) {
          return {
            code: `collect_${linkedWorkflow?.flowName}`,
            label: locale(keys.COLLECT_PAYMENT),
            icon: <PencilIcon />,
            onClick: () => {
              const isCustomRefferedEntity =
                REVYRE.COLLECT_IQ.DETAILS_PAGE_CUSTOM_REFFERED_ENTITIES.includes(
                  linkedWorkflow.workflowType,
                );
              dispatch(
                renderDialog({
                  id: REVYRE_FORM,
                  props: {
                    scenario: 'workflow',
                    type: 'update',
                    domainType: linkedWorkflow.workflowType,
                    workflowType,
                    ...(isCustomRefferedEntity && {
                      buttonMetadata: {
                        customUpdateState: 'collectPayment',
                        parentAssociateWorkflowType: workflowType,
                      },
                    }),
                  },
                }),
              );
            },
            buttonProps: {
              action: 'update',
            },
          };
        }
        return {
          code: `add_${linkedWorkflow?.flowName}`,
          label: `Add ${linkedWorkflow?.flowName}`,
          onClick: () => {
            const isCustomRefferedEntity =
              REVYRE.COLLECT_IQ.DETAILS_PAGE_CUSTOM_REFFERED_ENTITIES.includes(
                'workflow',
              );
            dispatch(
              renderDialog({
                id: REVYRE_FORM,
                props: {
                  scenario: 'workflow',
                  type: 'detailsCreate',
                  domainType: 'workflow',
                  workflowType: linkedWorkflow?.workflowType,
                  ...(isCustomRefferedEntity && {
                    buttonMetadata: {
                      parentAssociateWorkflowType: workflowType,
                    },
                  }),
                },
              }),
            );
          },
          buttonProps: {
            action: 'create',
          },
        };
      }),
    );
    return actions;
  }

  return actions;
};

export const getActionButtons = ({
  actions,
  getSingleButton,
  getPopupButton,
}: {
  actions: any[];
  getSingleButton: (action: any) => ModuleButton;
  getPopupButton: (actions: any[]) => ModuleButton;
}): ModuleButton[] => {
  if (!actions.length) return [];
  if (actions.length === 1) return [getSingleButton(actions[0])];
  return [getPopupButton(actions)];
};

export const getModuleButtons = (referredActions: any = []) => {
  const createActions = referredActions.filter(
    ({ buttonProps }) => buttonProps?.action === 'create',
  );
  const updateActions = referredActions.filter(
    ({ buttonProps }) => buttonProps?.action === 'update',
  );
  const customActions = referredActions.filter(
    ({ buttonProps }) => buttonProps?.action === 'custom',
  );

  return [
    ...getActionButtons({
      actions: createActions,
      getSingleButton: (action) => ({
        code: action.code,
        label: action.label,
        variant: 'default',
        icon: <AddIcon />,
        onClick: action.onClick,
      }),
      getPopupButton: (actions) => ({
        label: locale(Keys.ADD),
        variant: 'default',
        type: 'popup',
        icon: <AddIcon />,
        children: actions.map((action) => ({
          code: action.code,
          label: action.label,
          icon: <AddIcon />,
          onClick: action.onClick,
          variant: 'default',
        })),
      }),
    }),
    ...updateActions.map((action) => ({
      label: action?.label,
      onClick: action.onClick,
      variant: 'subtle',
      icon: action?.icon,
    })),
    ...getActionButtons({
      actions: customActions,
      getSingleButton: (action) => ({
        code: action.code,
        label: action.label,
        onClick: action.onClick,
        variant: 'subtle',
        icon: action?.icon,
      }),
      getPopupButton: (actions) => ({
        icon: <MenuIcon />,
        variant: 'subtle',
        type: 'popup',
        children: actions.map((action) => ({
          code: action.code,
          label: action.label,
          onClick: action.onClick,
          variant: 'subtle',
        })),
      }),
    }),
  ];
};

export const updateBreadcrumbs = (dispatch, props, inputsMap) => {
  if (props.revyre) {
    const { workflowType, domainType } = props;
    const typeCode = workflowType || domainType;

    const breadcrumbLabel = getRevyreModuleLabelByCode(typeCode);

    const revyreActiveModuleCodes = getRevyreWorkflowConfig()
      .filter(({ type, display }) => type !== 'HIERARCHY' && display !== false)
      .map(({ code }) => code);

    const shouldHide = !revyreActiveModuleCodes.includes(typeCode);

    if (domainType === 'workflow') {
      dispatch(
        setRouteModuleBreadCrumbParams({
          hidden: shouldHide,
          entityName: inputsMap?.customerName || inputsMap?.id,
          workflowName: _.upperCase(breadcrumbLabel),
        }),
      );
    } else {
      dispatch(
        setRouteModuleBreadCrumbParams({
          hidden: shouldHide,
          entityName:
            inputsMap?.customerName || inputsMap?.name || inputsMap?.id,
          domainName: _.capitalize(breadcrumbLabel),
        }),
      );
    }
  }
};

export const getRevyreReferredModules = (data) => {
  const { linkedReferralEntities } = data?.payload ?? {};

  return (linkedReferralEntities || []).filter(Boolean).map((module) => ({
    code: module.workflowType,
    name: module.flowName,
    size: module.entities?.length || 0,
    key: module.referralFilterKey,
  }));
};

export const getAllowedQuickActions = (actions, profiles) => {
  const allowedQuickActions = actions
    .filter((action) =>
      ['email', 'navigate', 'call', 'sms'].includes(action?.type),
    )
    .map((action) => transformQuickAction(action, profiles));
  return allowedQuickActions || [];
};
